import { useEffect, useState } from 'react'
import { toast } from 'react-toastify'
import { EyeIcon, EyeSlashIcon } from '@heroicons/react/24/outline'
import { AdminApi, AdminCourse, AdminSurveyFilter, AdminSurveyItem } from '../../services/adminApi'
import { formatDbDateTimeVi } from '../../utils/dbDateDisplay'

const stripHtml = (html?: string | null) => {
  if (!html) return ''
  return html
    .replace(/<[^>]*>/g, ' ')
    .replace(/&nbsp;/gi, ' ')
    .replace(/\s+/g, ' ')
    .trim()
}

const truncate = (text: string, max = 160) => (text.length > max ? `${text.slice(0, max)}…` : text)

const statusChipClass = (status: string) => {
  if (status === 'HIDDEN') return 'bg-red-100 text-red-700'
  return 'bg-m2-secondary/15 text-green-800'
}

const statusLabel = (status: string) => {
  if (status === 'HIDDEN') return 'Đã ẩn'
  if (status === 'SUBMITTED') return 'Hiển thị'
  return status || '-'
}

const renderStars = (rate: number) => '★'.repeat(Math.max(0, Math.min(5, rate))) + '☆'.repeat(Math.max(0, 5 - rate))

const AdminReviewsPage = () => {
  const [loading, setLoading] = useState(false)
  const [items, setItems] = useState<AdminSurveyItem[]>([])
  const [page, setPage] = useState(1)
  const [totalPages, setTotalPages] = useState(0)
  const [total, setTotal] = useState(0)
  const [courses, setCourses] = useState<AdminCourse[]>([])
  const [courseId, setCourseId] = useState<number | undefined>()
  const [status, setStatus] = useState<AdminSurveyFilter['status'] | ''>('')
  const [actingId, setActingId] = useState<number | null>(null)
  const [expandedId, setExpandedId] = useState<number | null>(null)

  const loadData = async (overrides?: {
    page?: number
    course_id?: number | undefined
    status?: AdminSurveyFilter['status'] | ''
  }) => {
    const nextPage = overrides?.page ?? page
    const nextCourseId = overrides && 'course_id' in overrides ? overrides.course_id : courseId
    const nextStatus = overrides && 'status' in overrides ? overrides.status : status

    setLoading(true)
    try {
      const res = await AdminApi.getSurveys({
        course_id: nextCourseId,
        status: nextStatus || undefined,
        page: nextPage,
        limit: 20,
      })
      setItems(res.data.items)
      setTotalPages(res.data.pagination.totalPages)
      setTotal(res.data.pagination.total)
    } catch {
      toast.error('Không tải được danh sách đánh giá')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    ;(async () => {
      try {
        const res = await AdminApi.getCourses()
        setCourses(res.data.items)
      } catch {
        toast.error('Không tải được danh sách khóa học')
      }
    })()
  }, [])

  useEffect(() => {
    void loadData({ page })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page])

  const applyFilters = () => {
    if (page === 1) {
      void loadData({ page: 1 })
    } else {
      setPage(1)
    }
  }

  const resetFilters = () => {
    setCourseId(undefined)
    setStatus('')
    if (page !== 1) setPage(1)
    void loadData({ page: 1, course_id: undefined, status: '' })
  }

  const handleToggleVisibility = async (row: AdminSurveyItem) => {
    const hide = !row.isHidden
    const confirmMsg = hide
      ? `Ẩn đánh giá #${row.id} của ${row.user.fullName || 'học viên'}? Đánh giá sẽ không hiển thị trên hệ thống.`
      : `Hiện lại đánh giá #${row.id} của ${row.user.fullName || 'học viên'} trên hệ thống?`
    if (!window.confirm(confirmMsg)) return

    setActingId(row.id)
    try {
      const res = await AdminApi.setSurveyVisibility(row.id, hide)
      if (res.success) {
        toast.success(res.message || (hide ? 'Đã ẩn đánh giá' : 'Đã hiện lại đánh giá'))
        void loadData({ page })
      } else {
        toast.error(res.message || 'Thao tác thất bại')
      }
    } catch {
      toast.error('Không gọi được API cập nhật hiển thị')
    } finally {
      setActingId(null)
    }
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-m2-on-surface">Quản lý đánh giá</h1>
          <p className="mt-1 text-sm text-m2-on-surface-variant">
            Xem danh sách đánh giá khóa học và ẩn các đánh giá không muốn hiển thị trên hệ thống.
          </p>
        </div>
        <div className="text-sm text-m2-on-surface-variant">Tổng: {total}</div>
      </div>

      <div className="rounded-lg border border-m2-outline-variant bg-m2-surface-container p-4">
        <div className="grid grid-cols-1 gap-3 md:grid-cols-4">
          <select
            className="rounded border border-m2-outline px-3 py-2 text-base"
            value={courseId ?? ''}
            onChange={(e) => setCourseId(e.target.value ? Number(e.target.value) : undefined)}
          >
            <option value="">Tất cả khóa học</option>
            {courses.map((course) => (
              <option key={course.id} value={course.id}>
                {course.code} - {course.name} {course.is_active || course.isActive ? '(ACTIVE)' : ''}
              </option>
            ))}
          </select>
          <select
            className="rounded border border-m2-outline px-3 py-2 text-base"
            value={status}
            onChange={(e) => setStatus((e.target.value || '') as AdminSurveyFilter['status'] | '')}
          >
            <option value="">Tất cả trạng thái</option>
            <option value="SUBMITTED">Hiển thị</option>
            <option value="HIDDEN">Đã ẩn</option>
          </select>
          <div className="flex gap-2 md:col-span-2">
            <button
              type="button"
              className="rounded bg-m2-primary px-3 py-2 text-base text-white hover:bg-eh-forest-shadow"
              onClick={applyFilters}
            >
              Tìm
            </button>
            <button
              type="button"
              className="rounded border border-m2-outline px-3 py-2 text-base text-m2-on-surface hover:bg-m2-surface-low"
              onClick={resetFilters}
            >
              Xóa lọc
            </button>
          </div>
        </div>
      </div>

      <div className="overflow-hidden rounded-lg border border-m2-outline-variant bg-m2-surface-container">
        <div className="overflow-x-auto">
          <table className="min-w-full text-base">
            <thead className="bg-m2-surface-low text-left text-sm uppercase text-m2-on-surface-variant">
              <tr>
                <th className="px-3 py-2">Thời gian</th>
                <th className="px-3 py-2">Học viên</th>
                <th className="px-3 py-2">Khóa học</th>
                <th className="px-3 py-2">Đánh giá</th>
                <th className="px-3 py-2">Nội dung</th>
                <th className="px-3 py-2">Trạng thái</th>
                <th className="px-3 py-2">Thao tác</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr>
                  <td className="px-3 py-4 text-center text-m2-on-surface-variant" colSpan={7}>
                    Đang tải...
                  </td>
                </tr>
              )}
              {!loading && items.length === 0 && (
                <tr>
                  <td className="px-3 py-4 text-center text-m2-on-surface-variant" colSpan={7}>
                    Không có dữ liệu
                  </td>
                </tr>
              )}
              {!loading &&
                items.map((row) => {
                  const plainNote = stripHtml(row.note)
                  const expanded = expandedId === row.id
                  return (
                    <tr key={row.id} className="border-t border-m2-outline-variant align-top">
                      <td className="px-3 py-2 text-sm text-m2-on-surface-variant whitespace-nowrap">
                        <div>#{row.id}</div>
                        <div>{formatDbDateTimeVi(row.createdAt)}</div>
                      </td>
                      <td className="px-3 py-2 text-sm">
                        <div className="font-medium text-m2-on-surface">{row.user.fullName || '-'}</div>
                        <div className="text-m2-on-surface-variant">{row.user.email || '-'}</div>
                        <div className="text-m2-on-surface-variant">{row.user.phone || '-'}</div>
                      </td>
                      <td className="px-3 py-2 text-sm">
                        <div className="font-medium text-m2-on-surface">{row.course.code || '-'}</div>
                        <div className="text-m2-on-surface-variant line-clamp-2">{row.course.name || '-'}</div>
                        {row.classroom.code ? (
                          <div className="mt-1 text-xs text-m2-on-surface-variant">Lớp: {row.classroom.code}</div>
                        ) : null}
                      </td>
                      <td className="px-3 py-2 text-sm whitespace-nowrap">
                        <div className="text-amber-500" title={`${row.rate}/5`}>
                          {renderStars(row.rate)}
                        </div>
                        <div className="text-m2-on-surface-variant">({row.rate}/5)</div>
                      </td>
                      <td className="px-3 py-2 text-sm text-m2-on-surface max-w-md">
                        {plainNote ? (
                          <>
                            <div>{expanded ? plainNote : truncate(plainNote)}</div>
                            {plainNote.length > 160 && (
                              <button
                                type="button"
                                className="mt-1 text-m2-primary hover:underline"
                                onClick={() => setExpandedId(expanded ? null : row.id)}
                              >
                                {expanded ? 'Thu gọn' : 'Xem thêm'}
                              </button>
                            )}
                          </>
                        ) : (
                          <span className="text-m2-on-surface-variant">Không có nội dung</span>
                        )}
                      </td>
                      <td className="px-3 py-2 text-sm">
                        <span className={`inline-flex rounded px-2 py-1 text-sm font-medium ${statusChipClass(row.status)}`}>
                          {statusLabel(row.status)}
                        </span>
                        {row.approvedAt && row.isHidden ? (
                          <div className="mt-1 text-xs text-m2-on-surface-variant">
                            Ẩn lúc: {formatDbDateTimeVi(row.approvedAt)}
                          </div>
                        ) : null}
                      </td>
                      <td className="px-3 py-2">
                        <button
                          type="button"
                          disabled={actingId === row.id}
                          onClick={() => void handleToggleVisibility(row)}
                          className={`inline-flex items-center gap-1 rounded border px-2 py-1 text-sm disabled:cursor-not-allowed disabled:opacity-60 ${
                            row.isHidden
                              ? 'border-emerald-600 text-emerald-800 hover:bg-emerald-50'
                              : 'border-red-600 text-m2-error hover:bg-m2-error/10'
                          }`}
                          title={row.isHidden ? 'Hiện lại đánh giá' : 'Ẩn đánh giá khỏi hệ thống'}
                        >
                          {row.isHidden ? (
                            <>
                              <EyeIcon className="h-5 w-5 shrink-0" aria-hidden />
                              Hiện lại
                            </>
                          ) : (
                            <>
                              <EyeSlashIcon className="h-5 w-5 shrink-0" aria-hidden />
                              Ẩn
                            </>
                          )}
                        </button>
                      </td>
                    </tr>
                  )
                })}
            </tbody>
          </table>
        </div>
      </div>

      <div className="flex items-center justify-end gap-2">
        <button
          type="button"
          className="rounded border border-m2-outline px-3 py-1 text-sm"
          disabled={page <= 1}
          onClick={() => setPage((p) => p - 1)}
        >
          Trước
        </button>
        <span className="text-sm text-m2-on-surface-variant">
          Trang {page} / {totalPages || 1}
        </span>
        <button
          type="button"
          className="rounded border border-m2-outline px-3 py-1 text-sm"
          disabled={page >= totalPages}
          onClick={() => setPage((p) => p + 1)}
        >
          Sau
        </button>
      </div>
    </div>
  )
}

export default AdminReviewsPage
